import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

export default function MemorialUpgrade() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [memorial, setMemorial] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [buyerEmail, setBuyerEmail] = useState("");
  const [payingStripe, setPayingStripe] = useState(false);
  const [payingPaypal, setPayingPaypal] = useState(false);

  useEffect(() => {
    Promise.all([
      apiClient.get(`/memorials/${slug}`),
      apiClient.get(`/config`),
    ])
      .then(([mRes, cRes]) => {
        setMemorial(mRes.data);
        setConfig(cRes.data);
        if (mRes.data?.is_premium) {
          toast("This memorial is already permanent ✦");
          nav(`/memorial/${slug}`, { replace: true });
        }
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Memorial not found"))
      .finally(() => setLoading(false));
  }, [slug, nav]);

  const startStripe = async () => {
    setPayingStripe(true);
    try {
      const { data } = await apiClient.post(
        `/memorials/${slug}/checkout/stripe/upgrade`,
        { origin_url: window.location.origin, buyer_email: buyerEmail || null }
      );
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setPayingStripe(false);
    }
  };

  const startPaypal = async () => {
    setPayingPaypal(true);
    try {
      const { data } = await apiClient.post(
        `/memorials/${slug}/checkout/paypal/upgrade`,
        { origin_url: window.location.origin, buyer_email: buyerEmail || null }
      );
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal is not available right now.");
      setPayingPaypal(false);
    }
  };

  if (loading) {
    return <div className="max-w-2xl mx-auto px-6 py-32 text-center font-serif-display italic text-[var(--rb-text-muted)]">One moment…</div>;
  }
  if (err) {
    return <div className="max-w-2xl mx-auto px-6 py-32 text-center font-serif-display italic text-[var(--rb-text-muted)]">{err}</div>;
  }
  if (!memorial) return null;

  const price = config?.memorial_upgrade_usd || 29;

  return (
    <main className="relative max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="memorial-upgrade-page">
      <div className="text-center mb-10">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Keep this memorial forever</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-[var(--rb-text-heading)] leading-tight">
          A permanent home for<br/><span className="italic">{memorial.pet_name}</span>.
        </h1>
        <p className="mt-5 font-serif-display italic text-[17px] text-[var(--rb-text-body)] max-w-xl mx-auto leading-relaxed">
          Pay once — never worry about their memorial disappearing. Loved ones can gather here for as long as the internet exists.
        </p>
      </div>

      <div className="hero-card rounded-sm p-8 md:p-12">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-3">What&apos;s unlocked</div>
            <ul className="space-y-2.5 font-serif-display italic text-[16px] text-[var(--rb-text-body)]">
              <li>✦ Permanent hosting — never expires</li>
              <li>✦ Public share link + eCard sending</li>
              <li>✦ Candle lighting by any visitor</li>
              <li>✦ Tribute wall for stories &amp; memories</li>
              <li>✦ AI-generated eulogy (Claude Sonnet 4.5)</li>
              <li>✦ Translate the poem into any language</li>
              <li>✦ Watermark is removed</li>
            </ul>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 mb-6 rounded-full border border-[var(--rb-gold)]/40 bg-white/70">
              <span className="text-[11px] tracking-[0.2em] uppercase text-[var(--rb-text-muted)]">One-time</span>
              <span className="font-serif-display text-5xl text-[var(--rb-gold)]">${price}</span>
            </div>

            <div className="mb-5">
              <label className="rb-label text-left block">Your email (for receipt — optional)</label>
              <input
                type="email"
                className="rb-input"
                value={buyerEmail}
                onChange={(e) => setBuyerEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="upgrade-buyer-email"
              />
            </div>

            <button
              onClick={startStripe}
              disabled={payingStripe || payingPaypal}
              className="btn-gold w-full justify-center mb-3"
              data-testid="upgrade-pay-stripe-btn"
            >
              {payingStripe ? "Redirecting to Stripe…" : `Pay $${price} with card`}
            </button>
            <button
              onClick={startPaypal}
              disabled={payingStripe || payingPaypal}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm border border-[#0070ba] text-[#0070ba] hover:bg-[#0070ba] hover:text-white transition-colors text-[13px] tracking-[0.22em] uppercase"
              data-testid="upgrade-pay-paypal-btn"
            >
              {payingPaypal ? "Redirecting to PayPal…" : "Pay with PayPal"}
            </button>

            <p className="text-[11px] text-[var(--rb-text-muted)] italic mt-4">
              Secure checkout · one-time · no subscription
            </p>
          </div>
        </div>
      </div>

      <div className="text-center mt-10">
        <a href={`/memorial/${slug}`} className="text-[12px] tracking-[0.22em] uppercase text-[var(--rb-text-muted)] hover:text-[var(--rb-text-heading)]">
          ← Back to memorial
        </a>
      </div>
    </main>
  );
}
